import { openDB } from 'idb';

// Increment the version number if you want to update the schema
// add or update the stores, change keyPath, add indexes, etc.
const schemaVersion = 1;

// Stores for the main user-specific database
const userStores = {
  OBJECTS: 'objects',
  DOCUMENTS: 'documents',
  DOCUMENT_IMAGES: 'documentImages',
  SYNC_INFO: 'syncInfo',
  PENDING_TASKS: 'pendingTasks',
  USER_DATA: 'userData',
  COMPANY_DATA: 'companyData',
};

// Stores for the general info database
const infoStores = {
  LAST_USER: 'lastUser',
  USER_STATUS: 'userStatus', // online or offline
};

export const initDB = async (userId) => {
  if (!userId) {
    throw new Error('User ID is required to initialize the database');
  }
  await initInfoDB();
  const dbName = `vision-offline-db-${userId}`;
  return openDB(dbName, schemaVersion, {
    upgrade(db) {
      if (!db.objectStoreNames.contains(userStores.OBJECTS)) {
        db.createObjectStore(userStores.OBJECTS, { keyPath: 'id' });
      }
      if (!db.objectStoreNames.contains(userStores.DOCUMENTS)) {
        db.createObjectStore(userStores.DOCUMENTS, { keyPath: 'id' });
      }
      if (!db.objectStoreNames.contains(userStores.DOCUMENT_IMAGES)) {
        const documentImageStore = db.createObjectStore(userStores.DOCUMENT_IMAGES, { keyPath: 'id', autoIncrement: true });
        documentImageStore.createIndex('documentId_nodeId', ['documentId', 'nodeId']);
      }
      if (!db.objectStoreNames.contains(userStores.SYNC_INFO)) {
        db.createObjectStore(userStores.SYNC_INFO, { keyPath: 'id' });
      }
      if (!db.objectStoreNames.contains(userStores.PENDING_TASKS)) {
        db.createObjectStore(userStores.PENDING_TASKS, { keyPath: 'taskId', autoIncrement: true });
      }
      if (!db.objectStoreNames.contains(userStores.USER_DATA)) {
        db.createObjectStore(userStores.USER_DATA, { keyPath: 'storeKey' });
      }
      if (!db.objectStoreNames.contains(userStores.COMPANY_DATA)) {
        db.createObjectStore(userStores.COMPANY_DATA, { keyPath: 'storeKey' });
      }
    },
  });
};

export const initInfoDB = async () => {
  const offlineInfoDB = 'vision-offline-info-db';
  return openDB(offlineInfoDB, schemaVersion, {
    upgrade(db) {
      if (!db.objectStoreNames.contains(infoStores.LAST_USER)) {
        db.createObjectStore(infoStores.LAST_USER, { keyPath: 'id' });
      }
      if (!db.objectStoreNames.contains(infoStores.USER_STATUS)) {
        db.createObjectStore(infoStores.USER_STATUS, { keyPath: 'id' });
      }
    },
  });
};

// Function to set user data
export const setUserData = async (userId, data) => {
  const db = await initDB(userId);
  const tx = db.transaction(userStores.USER_DATA, 'readwrite');
  const store = tx.objectStore(userStores.USER_DATA);
  await store.put({ storeKey: 'user', ...data });
  await tx.done;
};

// Function to get user data
export const getUserData = async (userId) => {
  const db = await initDB(userId);
  const tx = db.transaction(userStores.USER_DATA, 'readonly');
  const store = tx.objectStore(userStores.USER_DATA);
  const data = await store.get('user');
  return data;
};

// Function to set company data
export const setCompanyData = async (userId, data) => {
  const db = await initDB(userId);
  const tx = db.transaction(userStores.COMPANY_DATA, 'readwrite');
  const store = tx.objectStore(userStores.COMPANY_DATA);
  await store.put({ storeKey: 'company', ...data });
  await tx.done;
};

// Function to get company data
export const getCompanyData = async (userId) => {
  const db = await initDB(userId);
  const tx = db.transaction(userStores.COMPANY_DATA, 'readonly');
  const store = tx.objectStore(userStores.COMPANY_DATA);
  const data = await store.get('company');
  return data;
};

export const setLastUser = async (userId) => {
  const db = await initInfoDB();
  const tx = db.transaction(infoStores.LAST_USER, 'readwrite');
  const store = tx.objectStore(infoStores.LAST_USER);
  await store.put({ id: 'lastUser', userId });
  await tx.done;
};

export const getLastUser = async () => {
  const db = await initInfoDB();
  const tx = db.transaction(infoStores.LAST_USER, 'readonly');
  const store = tx.objectStore(infoStores.LAST_USER);
  const data = await store.get('lastUser');
  return data ? data.userId : null;
};

export const setUserStatus = async (status) => {
  const db = await initInfoDB();
  const tx = db.transaction(infoStores.USER_STATUS, 'readwrite');
  const store = tx.objectStore(infoStores.USER_STATUS);
  await store.put({ id: 'userStatus', status });
  await tx.done;
};

export const getUserStatus = async () => {
  const db = await initInfoDB();
  const tx = db.transaction(infoStores.USER_STATUS, 'readonly');
  const store = tx.objectStore(infoStores.USER_STATUS);
  const data = await store.get('userStatus');
  return data ? data.status : null;
};

const withDB = async (userId, callback) => {
  try {
    const db = await initDB(userId);
    return await callback(db);
  } catch (error) {
    if (isStorageError(error)) {
      console.error('Storage quota exceeded:', error);
      throw new Error('STORAGE_FULL');
    } else {
      console.error('Error during DB operation:', error);
      throw error;
    }
  }
};

// Utility function to detect storage errors
const isStorageError = (error) => {
  return (
    error.name === 'QuotaExceededError' || 
    error.message.includes('quota') ||
    error.message.includes('storage') ||
    error.message.includes('full disk')
  );
};

export const addDataToDB = async (userId, storeName, data) => {
  return withDB(userId, async (db) => {
    const tx = db.transaction(storeName, 'readwrite');
    const store = tx.objectStore(storeName);
    await store.put(data);
    await tx.done;
  });
};

export const getDBSyncStatus = async (userId) => {
  return withDB(userId, async (db) => {
    if (!db.objectStoreNames.contains(userStores.SYNC_INFO)) {
      return false;
    }

    const tx = db.transaction(userStores.SYNC_INFO, 'readonly');
    const store = tx.objectStore(userStores.SYNC_INFO);
    const syncInfo = await store.get('syncInfo');

    return syncInfo?.lastSynced || false;
  });
};

export const getStoreCount = async (userId, storeName) => {
  return withDB(userId, async (db) => {
    const tx = db.transaction(storeName, 'readonly');
    const store = tx.objectStore(storeName);
    const count = await store.count();
    return count;
  });
};

export const addMultipleDataToDB = async (userId, storeName, dataArray) => {
  if (!dataArray || !dataArray.length) return;

  return withDB(userId, async (db) => {
    const BATCH_SIZE = 50;
    let successCount = 0;

    for (let i = 0; i < dataArray.length; i += BATCH_SIZE) {
      const batch = dataArray.slice(i, i + BATCH_SIZE);
      try {
        const tx = db.transaction(storeName, 'readwrite');
        const store = tx.objectStore(storeName);
        
        for (const item of batch) {
          await store.put(item);
        }
        
        await tx.done;
        successCount += batch.length;
      } catch (error) {
        if (error.name === 'QuotaExceededError' || error.message.includes('full disk')) {
          console.error(`Storage quota exceeded after storing ${successCount} items`);
          throw new Error('STORAGE_FULL');
        } else {
          throw error;
        }
      }
    }
    return successCount;
  });
};

export const getDataFromDB = async (userId, storeName, id) => {
  return withDB(userId, async (db) => {
    const tx = db.transaction(storeName, 'readonly');
    const store = tx.objectStore(storeName);
    const data = await store.get(id);
    return data ? { ...data } : null;
  });
};

export const getMultipleDataFromDB = async (userId, storeName, ids) => {
  return withDB(userId, async (db) => {
    const tx = db.transaction(storeName, 'readonly');
    const store = tx.objectStore(storeName);
    const results = [];

    for (const id of ids) {
      const data = await store.get(id);
      if (data) {
        results.push({ ...data });
      }
    }

    return results;
  });
};

export const getAllDataFromDB = async (userId, storeName) => {
  return withDB(userId, async (db) => {
    const tx = db.transaction(storeName, 'readonly');
    const store = tx.objectStore(storeName);
    const allRecords = await store.getAll();
    return allRecords;
  });
};

export const clearStore = async (userId, storeName) => {
  return withDB(userId, async (db) => {
    const tx = db.transaction(storeName, 'readwrite');
    const store = tx.objectStore(storeName);

    await store.clear();
    await tx.done;
  });
};

export const deleteDataFromDB = async (userId, storeName, id) => {
  return withDB(userId, async (db) => {
    const tx = db.transaction(storeName, 'readwrite');
    const store = tx.objectStore(storeName);

    await store.delete(id);
    await tx.done;
  });
};

export const deleteDataByCriteria = async (userId, storeName, criteria) => {
  return withDB(userId, async (db) => {
    const tx = db.transaction(storeName, 'readwrite');
    const store = tx.objectStore(storeName);

    const allRecords = await store.getAll();
    for (const record of allRecords) {
      let match = true;
      for (const key in criteria) {
        if (Object.prototype.hasOwnProperty.call(criteria, key) && record[key] !== criteria[key]) {
          match = false;
          break;
        }
      }
      if (match) {
        await store.delete(record.id);
      }
    }

    await tx.done;
  });
};

export const deleteTasksByCriteria = async (userId, criteria) => {
  return withDB(userId, async (db) => {
    const tx = db.transaction(userStores.PENDING_TASKS, 'readwrite');
    const store = tx.objectStore(userStores.PENDING_TASKS);

    const allRecords = await store.getAll();
    for (const record of allRecords) {
      let match = true;
      for (const key in criteria) {
        if (Object.prototype.hasOwnProperty.call(criteria, key) && record[key] !== criteria[key]) {
          match = false;
          break;
        }
      }
      if (match) {
        await store.delete(record.taskId);
      }
    }

    await tx.done;
  });
};

export const getTaskByCriteria = async (userId, criteria) => {
  return withDB(userId, async (db) => {
    const tx = db.transaction(userStores.PENDING_TASKS, 'readonly');
    const store = tx.objectStore(userStores.PENDING_TASKS);

    const allRecords = await store.getAll();
    // Loop through all records in the store
    for (const record of allRecords) {
      let match = true;
      // Loop through all criteria
      for (const key in criteria) {
        // If the criteria has the key and the record does not match the criteria, set match to false and break
        if (Object.prototype.hasOwnProperty.call(criteria, key) && record[key] !== criteria[key]) {
          match = false;
          break;
        }
      }
      if (match) {
        return { ...record };
      }
    }

    return null;
  });
};

export const getTasksByCriteria = async (userId, criteria) => {
  return withDB(userId, async (db) => {
    const tx = db.transaction(userStores.PENDING_TASKS, 'readonly');
    const store = tx.objectStore(userStores.PENDING_TASKS);

    const allRecords = await store.getAll();
    const matchingRecords = [];

    // Loop through all records in the store
    for (const record of allRecords) {
      let match = true;
      // Loop through all criteria
      for (const key in criteria) {
        // If the criteria has the key and the record does not match the criteria, set match to false and break
        if (Object.prototype.hasOwnProperty.call(criteria, key) && record[key] !== criteria[key]) {
          match = false;
          break;
        }
      }
      if (match) {
        matchingRecords.push({ ...record });
      }
    }

    return matchingRecords;
  });
};

export const updateNodeIdInIndexedDB = async (userId, oldNodeId, newNodeId) => {
  return withDB(userId, async (db) => {
    const tx = db.transaction(userStores.PENDING_TASKS, 'readwrite');
    const store = tx.objectStore(userStores.PENDING_TASKS);

    const allRecords = await store.getAll();
    for (const record of allRecords) {
      if (
        record.tempId === oldNodeId &&
        record.nodeId &&
        record.nodeId.startsWith('temp-')
      ) {
        const updatedRecord = { ...record, nodeId: newNodeId };
        await store.put(updatedRecord);
      }
    }

    await tx.done;
  });
}

export const getImageData = async (userId, criteria) => {
  const { documentId, nodeId } = criteria;
  return withDB(userId, async (db) => {
    const tx = db.transaction(userStores.DOCUMENT_IMAGES, 'readonly');
    const store = tx.objectStore(userStores.DOCUMENT_IMAGES);

    const index = store.index('documentId_nodeId');
    const matchingRecord = await index.get([documentId, nodeId]);

    return matchingRecord ? { ...matchingRecord } : null;
  });
};